'use client'

/**
 * ThemeSelector — named desk themes + day/night mode.
 * panel = Settings full grid · trigger = navbar dropdown · compact = denser grid
 */

import React, { useCallback, useId, useMemo, useState } from 'react'
import { CheckOutlined, DownOutlined } from '@ant-design/icons'
import { Dropdown } from 'antd'
import Link from 'next/link'
import { useTheme } from '@/shared/theme/provider/ThemeProvider'
import {
  getThemeCatalogEntry,
  listThemeCatalog,
  type ThemeCatalogEntry,
} from '@/shared/theme/catalog'
import type { ColorTheme, ThemeMode } from '@/shared/types/theme.types'
import styles from './ThemeSelector.module.css'

export type ThemeSelectorVariant = 'panel' | 'compact' | 'trigger'

export interface ThemeSelectorProps {
  variant?: ThemeSelectorVariant
  showMode?: boolean
  showDescription?: boolean
  className?: string
  onThemeChange?: (id: ColorTheme) => void
  onModeChange?: (mode: ThemeMode) => void
}

const MODES: { id: ThemeMode; label: string; hint: string }[] = [
  { id: 'light', label: 'Day', hint: 'Light variant' },
  { id: 'dark', label: 'Night', hint: 'Dark variant' },
  { id: 'auto', label: 'System', hint: 'Follow OS' },
]

function SwatchStrip({
  entry,
  className = '',
}: {
  entry: ThemeCatalogEntry
  className?: string
}) {
  const s = entry.swatches
  return (
    <div className={`${styles.swatchRow} ${className}`.trim()} aria-hidden>
      <span className={styles.swatch} style={{ background: s.bg }} />
      <span className={styles.swatch} style={{ background: s.primary }} />
      <span className={styles.swatch} style={{ background: s.bull }} />
      <span className={styles.swatch} style={{ background: s.bear }} />
    </div>
  )
}

function ThemeGrid({
  compact,
  showDescription,
  colorTheme,
  onPick,
}: {
  compact: boolean
  showDescription: boolean
  colorTheme: ColorTheme
  onPick: (id: ColorTheme) => void
}) {
  const catalog = useMemo(() => listThemeCatalog(), [])
  const listId = useId()

  return (
    <div
      className={styles.grid}
      role="listbox"
      aria-label="Desk themes"
      id={listId}
    >
      {catalog.map((entry) => {
        const active = entry.id === colorTheme
        return (
          <button
            key={entry.id}
            type="button"
            role="option"
            aria-selected={active}
            className={`${styles.card} ${active ? styles.cardActive : ''}`}
            onClick={() => onPick(entry.id)}
          >
            <SwatchStrip entry={entry} />
            <div className={styles.meta}>
              <span className={styles.name}>{entry.name}</span>
              {showDescription && !compact && (
                <span className={styles.desc}>{entry.description}</span>
              )}
              {active && <span className={styles.badge}>Active</span>}
            </div>
          </button>
        )
      })}
    </div>
  )
}

function ThemeList({
  colorTheme,
  onPick,
}: {
  colorTheme: ColorTheme
  onPick: (id: ColorTheme) => void
}) {
  const catalog = useMemo(() => listThemeCatalog(), [])

  return (
    <ul className={styles.themeList} role="listbox" aria-label="Desk themes">
      {catalog.map((entry) => {
        const active = entry.id === colorTheme
        return (
          <li key={entry.id}>
            <button
              type="button"
              role="option"
              aria-selected={active}
              className={`${styles.themeRow} ${active ? styles.themeRowActive : ''}`}
              onClick={() => onPick(entry.id)}
            >
              <SwatchStrip entry={entry} className={styles.themeRowSwatch} />
              <span className={styles.themeRowCopy}>
                <span className={styles.themeRowName}>{entry.name}</span>
                <span className={styles.themeRowDesc}>{entry.description}</span>
              </span>
              {active ? (
                <CheckOutlined className={styles.themeRowCheck} aria-hidden />
              ) : (
                <span className={styles.themeRowCheckPlaceholder} aria-hidden />
              )}
            </button>
          </li>
        )
      })}
    </ul>
  )
}

function ModePicker({
  mode,
  onPick,
  layout,
}: {
  mode: ThemeMode
  onPick: (next: ThemeMode) => void
  layout: 'inline' | 'segmented'
}) {
  if (layout === 'segmented') {
    return (
      <div
        className={styles.modeSegmented}
        role="group"
        aria-label="Day or night mode"
      >
        {MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            className={`${styles.modeSegment} ${mode === m.id ? styles.modeSegmentActive : ''}`}
            aria-pressed={mode === m.id}
            title={m.hint}
            onClick={() => onPick(m.id)}
          >
            {m.label}
          </button>
        ))}
      </div>
    )
  }

  return (
    <div className={styles.modeRow} role="group" aria-label="Day or night mode">
      {MODES.map((m) => (
        <button
          key={m.id}
          type="button"
          className={`${styles.modeBtn} ${mode === m.id ? styles.modeBtnActive : ''}`}
          aria-pressed={mode === m.id}
          title={m.hint}
          onClick={() => onPick(m.id)}
        >
          {m.label}
        </button>
      ))}
    </div>
  )
}

function variantLabel(mode: ThemeMode, type: 'light' | 'dark'): string {
  if (mode === 'auto') return 'System'
  if (mode === 'light') return 'Day'
  return 'Night'
}

export function ThemeSelector({
  variant = 'panel',
  showMode = true,
  showDescription = true,
  className = '',
  onThemeChange,
  onModeChange,
}: ThemeSelectorProps) {
  const { mode, type, colorTheme, setMode, setColorTheme } = useTheme()
  const current = getThemeCatalogEntry(colorTheme)
  const compact = variant === 'compact'
  const isTrigger = variant === 'trigger'

  const pickTheme = useCallback(
    (id: ColorTheme) => {
      setColorTheme(id)
      onThemeChange?.(id)
    },
    [setColorTheme, onThemeChange]
  )

  const pickMode = useCallback(
    (next: ThemeMode) => {
      setMode(next)
      onModeChange?.(next)
    },
    [setMode, onModeChange]
  )

  const body = isTrigger ? (
    <div className={styles.dropdownBody}>
      <header className={styles.dropdownHead}>
        <p className={styles.dropdownTitle}>Appearance</p>
        <p className={styles.dropdownLead}>
          Named desk themes. Day / night picks the light or dark variant.
        </p>
      </header>

      {showMode && (
        <section className={styles.dropdownSection} aria-labelledby="theme-mode-label">
          <p className={styles.sectionLabel} id="theme-mode-label">
            Day / night
          </p>
          <ModePicker mode={mode} onPick={pickMode} layout="segmented" />
        </section>
      )}

      <section className={styles.dropdownSection} aria-labelledby="theme-palette-label">
        <p className={styles.sectionLabel} id="theme-palette-label">
          Desk theme
        </p>
        <ThemeList colorTheme={colorTheme} onPick={pickTheme} />
      </section>

      <footer className={styles.dropdownFoot}>
        <p className={styles.footer}>
          {current.name} · {variantLabel(mode, type).toLowerCase()} variant
          {mode === 'auto' ? ' (follows system)' : ''}
        </p>
        <Link href="/settings#settings-appearance" className={styles.settingsLink}>
          All appearance settings
        </Link>
      </footer>
    </div>
  ) : (
    <div
      className={`${styles.root} ${compact ? styles.compact : ''} ${className}`.trim()}
    >
      {showMode && (
        <div>
          <p className={styles.sectionLabel}>Day / night</p>
          <ModePicker mode={mode} onPick={pickMode} layout="inline" />
        </div>
      )}

      <div>
        <p className={styles.sectionLabel}>Desk theme</p>
        <ThemeGrid
          compact={compact}
          showDescription={showDescription}
          colorTheme={colorTheme}
          onPick={pickTheme}
        />
      </div>

      <p className={styles.footer}>
        Active desk theme: {current.name} · {variantLabel(mode, type).toLowerCase()} variant
        {mode === 'auto' ? ' (follows system)' : ''}
      </p>
    </div>
  )

  if (!isTrigger) {
    return body
  }

  return (
    <ThemeSelectorTrigger
      current={current}
      modeLabel={variantLabel(mode, type)}
      panel={body}
    />
  )
}

function ThemeSelectorTrigger({
  current,
  modeLabel,
  panel,
}: {
  current: ThemeCatalogEntry
  modeLabel: string
  panel: React.ReactNode
}) {
  const [open, setOpen] = useState(false)

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      placement="bottomRight"
      destroyOnHidden
      classNames={{ root: styles.dropdownOverlay }}
      popupRender={() => <div className={styles.panel}>{panel}</div>}
    >
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Desk theme ${current.name}, ${modeLabel} variant. Open appearance menu.`}
      >
        <SwatchStrip entry={current} className={styles.triggerSwatches} />
        <span className={styles.triggerCopy}>
          <span className={styles.triggerTheme}>{current.name}</span>
          <span className={styles.triggerMode}>{modeLabel}</span>
        </span>
        <DownOutlined className={styles.triggerChevron} aria-hidden />
      </button>
    </Dropdown>
  )
}

export default ThemeSelector
